import { axiosJSONInstance } from '@/config';
import {
  CarrierPreviewResponse,
  CarrierPreviewRequest,
  CarrierIndexSettingResponse,
  AvailableCarriersRequest,
  AvailableCarriersResponse,
  AvailableCarrierItem,
  CarrierPreviewItem,
  ServiceTypeEnum,
  CarrierRestrictedItemsResponse,
} from '@/types';

// Get carriers index setting
export const getCarriersIndexSetting = async (): Promise<CarrierIndexSettingResponse> => {
  const response = await axiosJSONInstance.get('/merchants/carriers/index-setting');
  return response.data;
};

// Toggle carrier active status
export const toggleCarrierActive = async (uid: string): Promise<CarrierIndexSettingResponse> => {
  const response = await axiosJSONInstance.post(`/merchants/carriers/${uid}/toggle-active`);
  return response.data;
};

// Preview carriers by order data
export const previewCarrierByOrderData = async (
  request: CarrierPreviewRequest,
): Promise<CarrierPreviewResponse> => {
  const response = await axiosJSONInstance.post('/merchants/dispatcher/preview', request);
  return response.data;
};

// Adapter: maps new AvailableCarrierItem → legacy CarrierPreviewItem shape expected by UI
const adaptAvailableCarrierItem = (item: AvailableCarrierItem): CarrierPreviewItem => ({
  shipping_fee: String(item.shipping_fee),
  if_ship_before: item.same_day_cutoff ?? item.drop_off_time ?? '',
  average_delivery_time: item.delivery_day === 'same' ? 'same_day' : 'next_day',
  next_day_delivery: item.delivery_day === 'next' ? (item.drop_off_time ?? '') : '',
  next_day_delivery_rate: '',
  carrier: {
    uid: item.carrier_uid, // RS-22: thread the carrier UID so rule filters can match
    title: item.carrier_name,
    slug: item.carrier_slug,
    avatar: { abs_url: item.carrier_logo_url } as any,
  } as any,
  serviceType: item.delivery_day === 'same' ? ServiceTypeEnum.SAME_DAY : ServiceTypeEnum.NEXT_DAY,
  // TEMP-9: this adapter whitelists fields, so the cold-chain capability
  // must be threaded explicitly or the rate-shopper badge never renders.
  supports_temperature_control: item.supports_temperature_control,
});

// Adapter: maps AvailableCarriersResponse → legacy CarrierPreviewResponse shape
const adaptAvailableCarriersResponse = (
  raw: AvailableCarriersResponse,
): CarrierPreviewResponse & { process_id: string; recommended_carrier_slug: string } => {
  const sameDayCarriers = raw.object.available_carriers
    .filter((c) => c.delivery_day === 'same')
    .map(adaptAvailableCarrierItem);

  const nextDayCarriers = raw.object.available_carriers
    .filter((c) => c.delivery_day === 'next')
    .map(adaptAvailableCarrierItem);

  return {
    status: raw.status,
    code: raw.code,
    message: raw.message,
    object: {
      if_deliver_before: '',
      same_day_carriers: sameDayCarriers,
      next_day_carriers: nextDayCarriers,
    },
    process_id: raw.object.process_id,
    recommended_carrier_slug: raw.object.recommended_carrier_slug,
  };
};

// Get available carriers for order data (new Phase 2 endpoint)
// Returns legacy CarrierPreviewResponse shape so all existing consumers are unaffected
export const getAvailableCarriers = async (
  request: AvailableCarriersRequest,
): Promise<CarrierPreviewResponse & { process_id: string; recommended_carrier_slug: string }> => {
  const response = await axiosJSONInstance.post('/merchants/orders/available-carriers', request);
  return adaptAvailableCarriersResponse(response.data as AvailableCarriersResponse);
};

// Preview carriers by order ID
export const previewCarrierByOrderId = async (
  orderUid: string,
): Promise<CarrierPreviewResponse> => {
  const response = await axiosJSONInstance.post(
    `/merchants/dispatcher/preview/by-order/${orderUid}`,
  );
  return response.data;
};

// Get restricted items for a carrier by slug
export const fetchCarrierRestrictedItems = async (
  slug: string,
): Promise<CarrierRestrictedItemsResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/carriers/${slug}/restricted-items`);
  return response.data;
};

// Preview carriers by order excel row
export const previewCarrierByOrderExcelRow = async (
  rowKey: string,
  pickupAddressRegionUid: string,
): Promise<CarrierPreviewResponse> => {
  const response = await axiosJSONInstance.post(
    `/merchants/dispatcher/preview/by-order-excel-row`,
    { order_excel_row_uid: rowKey, source_region_uid: pickupAddressRegionUid },
  );
  return response.data;
};
